import SnsFacebook from '@/assets/img/anniversary/ico_sns_facebook.png';
import SnsInstar from '@/assets/img/anniversary/ico_sns_instragram.png';
import SnsKakao from '@/assets/img/anniversary/ico_sns_kakakotalk.png';
// import SnsTwitter from '@/assets/img/sns_twitter@2x.png';
import ImgNodata from '@/assets/img/anniversary/img_nodata.png';
import AnchorText from '@/components/AnchorText';
import Image from '@/components/Image';
import InputText from '@/components/Input/InputText';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { usePopupStore } from '@/stores/PopupStore';
import { Link } from 'gatsby';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

const KAKAO_SDK_URL = `https://developers.kakao.com/sdk/js/kakao.min.js`;
const FACEBOOK_SDK_URL = `https://connect.facebook.net/en_US/sdk.js`;
const ButtonFlex = styled.ul`
  display: flex;
  justify-content: center;

  & > li {
    width: 41px;

    &:not(:first-child) {
      margin: 0 0 0 10px;
    }

    & > ${AnchorText} {
      width: 100%;
    }

    & button.sns-share-insta {
      background: url(${SnsInstar}) no-repeat;
      font-size: 0;
      width: 41px;
      height: 41px;
    }

    ${breakpoint(`tablet`)} {
      &:not(:first-child) {
        margin: 0 0 0 15px;
      }
    }
  }
`;
const CopyUrlFlex = styled.ul`
  margin: 15px 0;
  display: flex;
  justify-content: center;

  & > li {
    height: 40px;
    &.copy-input {
      flex: calc(100% - 85px);

      & > ${InputText} {
        font-family: 'Noto Sans CJK KR';
        font-size: 14px;
        text-overflow: ellipsis;
        letter-spacing: -0.4px;
        height: 100%;
        width: 100%;

        &[readonly] {
          background: #fff;
          border: 1px solid #e5e6e8;
          opacity: 1;
        }
      }
    }
    &.copy-btn {
      flex: 85px;

      & > ${AnchorText} {
        font-family: 'Noto Sans CJK KR';
        font-size: 14px;
        font-weight: bold;
        width: 100%;
        height: 100%;
        background: #fff;
        color: #1cabe2;
        border: solid 1px #1cabe2;
        text-decoration: none;
      }
    }
  }
`;

interface SnsButtonProps {
  text: string;
  url: string;
  imgUrl: string;
}
/*
interface FacebookLoginInfoProps extends ReactFacebookLoginInfo {
  error?: any;
}
*/
const AnniProjectSnsButton: FC<SnsButtonProps> = observer(
  ({ text, url, imgUrl }) => {
    const popupStore = usePopupStore();

    const shareKakao = useCallback(() => {
      // v2.0
      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: text || `유니세프`,
          description: '',
          imageUrl: imgUrl,
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      });
    }, [text, url, imgUrl]);

    const initKakao = useCallback(() => {
      if (process.env.KAKAO_JAVASCRIPT_KEY) {
        window.Kakao.init(process.env.KAKAO_JAVASCRIPT_KEY);
      }
    }, []);

    const loadKakaoScript = useCallback(() => {
      if (document.querySelectorAll(`#kakao-sdk`).length === 0) {
        const script = document.createElement(`script`);
        script.id = `kakao-sdk`;
        script.src = KAKAO_SDK_URL;
        script.onload = initKakao;
        document.head.appendChild(script);
      }
    }, [initKakao]);

    useEffect(() => {
      loadKakaoScript();
    }, [loadKakaoScript]);

    const copyToClipboard = () => {
      const t = document.createElement(`textarea`);
      document.body.appendChild(t);
      t.value = url;
      t.select();
      document.execCommand(`copy`);
      document.body.removeChild(t);
      popupStore.show(`클립보드로 복사되었습니다`);
    };

    /* --------------------------------------------------------
     *                      FACEBOOK LOGIN (https://gist.github.com/pixochi/fb82189a1d73381a0df1949305ff0390)
     * --------------------------------------------------------
     */
    const [facebookUserAccessToken, setFacebookUserAccessToken] = useState('');
    const loadFacebookScript = useCallback(() => {
      if (document.querySelectorAll(`#facebook-sdk`).length === 0) {
        const script = document.createElement(`script`);
        script.id = `facebook-sdk`;
        script.src = FACEBOOK_SDK_URL;
        script.onload = () => {
          if (process.env.FACEBOOK_SHARE_APP_ID) {
            window.FB.init({
              appId: process.env.FACEBOOK_SHARE_APP_ID,
              cookie: true,
              xfbml: true,
              version: process.env.FACEBOOK_SHARE_APP_SDK_VER,
            });
            window.FB.getLoginStatus((response: any) => {
              setFacebookUserAccessToken(response.authResponse?.accessToken);
            });
          }
        };
        document.head.appendChild(script);
      }
    }, []);
    useEffect(() => {
      loadFacebookScript();
    }, [loadFacebookScript]);

    /* --------------------------------------------------------
     *             INSTAGRAM AND FACEBOOK GRAPH APIs
     * --------------------------------------------------------
     */
    const getFacebookPages = () =>
      new Promise((resolve) => {
        window.FB.api(
          'me/accounts',
          { access_token: facebookUserAccessToken },
          (response: any) => {
            resolve(response.data);
          },
        );
      });

    const getInstagramAccountId = (facebookPageId: any) =>
      new Promise((resolve) => {
        window.FB.api(
          facebookPageId,
          {
            access_token: facebookUserAccessToken,
            fields: 'instagram_business_account',
          },
          (response: any) => {
            resolve(response?.instagram_business_account);
          },
        );
      });

    const createMediaObjectContainer = (instagramAccountId: any) =>
      new Promise((resolve) => {
        window.FB.api(
          `${instagramAccountId}/media`,
          'post',
          {
            access_token: facebookUserAccessToken,
            image_url: imgUrl,
            caption: text,
          },
          (response: any) => {
            resolve(response.id);
          },
        );
      });

    const publishMediaObjectContainer = (
      instagramAccountId: any,
      mediaObjectContainerId: any,
    ) =>
      new Promise((resolve) => {
        window.FB.api(
          `${instagramAccountId}/media_publish`,
          'post',
          {
            access_token: facebookUserAccessToken,
            creation_id: mediaObjectContainerId,
          },
          (response: any) => {
            resolve(response.id);
          },
        );
      });

    const shareInstagramPost = async () => {
      console.log('imgUrl', imgUrl, ImgNodata);
      if (!imgUrl || imgUrl.indexOf(ImgNodata) > -1) {
        popupStore.show(
          '이미지가 없는 경우에는 인스타그램 공유 할 수 없습니다.',
        );
        return;
      }
      /** 페이스북 비로그인시 로그인창 오픈 */
      if (!facebookUserAccessToken) {
        window.FB.login(
          (response: any) => {
            setFacebookUserAccessToken(response.authResponse?.accessToken);
          },
          {
            // Scopes that allow us to publish content to Instagram
            scope: 'instagram_basic,pages_show_list,instagram_content_publish',
          },
        );
        return;
      }

      /** 페이스북 연결 페이지 확인 */
      const facebookPages: any = await getFacebookPages();
      if (!facebookPages || !Array.isArray(facebookPages)) {
        popupStore.show('연결된 페이스북 페이지가 없습니다.');
        return;
      }

      /** 인스타그램 계정 연결 확인 */
      let instagramAccount: any = [];
      const getInstarAccount = facebookPages.map(async (item: any) => {
        const account = await getInstagramAccountId(item.id);
        if (account) instagramAccount.push(account);
      });
      await Promise.all(getInstarAccount);
      if (instagramAccount.length === 0) {
        popupStore.show('연결된 인스타그램 계정이 없습니다.');
        return;
      }

      /** 연결된 인스타그램 계정중 첫번째 계정에 게시글 임시 등록 */
      const instagramAccountId = instagramAccount[0].id;
      const mediaObjectContainerId = await createMediaObjectContainer(
        instagramAccountId,
      );
      if (!mediaObjectContainerId) {
        popupStore.show('인스타그램 게시글 등록에 실패했습니다.');
        return;
      }

      /** 연결된 인스타그램 계정중 첫번째 계정에 게시글 임시글 게시 요청 */
      const instagramPublish = await publishMediaObjectContainer(
        instagramAccountId,
        mediaObjectContainerId,
      );
      if (!instagramPublish) {
        popupStore.show('인스타그램 게시글 게시에 실패했습니다.');
      } else {
        popupStore.show('인스타그램 게시글 게시되었습니다.');
      }

      /** 페이스북 로그아웃 처리
       * 매번 페이스북 로그인을 시키려면 주석 해제 필요
       * 
      
      window.FB.logout(() => {
        setFacebookUserAccessToken(undefined);
      });

      *
      */
    };

    return (
      <>
        <ButtonFlex>
          <li>
            <Link
              to={`http://www.facebook.com/sharer/sharer.php?u=${url}`}
              target="_blank"
            >
              <Image
                pcSrc={SnsFacebook}
                mobileSrc={SnsFacebook}
                alt="페이스북 공유하기"
              />
            </Link>
          </li>
          <li>
            <AnchorText onClick={shareKakao}>
              <Image
                pcSrc={SnsKakao}
                mobileSrc={SnsKakao}
                alt="카카오톡 공유하기"
              />
            </AnchorText>
          </li>
          {/*
      <li>
        <Link
          to={`https://twitter.com/intent/tweet?text=${text || ``}&url=${url}`}
          target="_blank"
        >
          <Image
            pcSrc={SnsTwitter}
            mobileSrc={SnsTwitter}
            alt="트위터 공유하기"
          />
        </Link>
      </li>
      */}
          <li>
            <button
              type="button"
              onClick={shareInstagramPost}
              className="sns-share-insta"
            >
              인스타 공유하기
            </button>
          </li>
        </ButtonFlex>
        <CopyUrlFlex>
          <li className="copy-input">
            <InputText
              name="aprjtName"
              placeholder="프로젝트명 입력"
              defaultValue={url}
              readOnly
            />
          </li>
          <li className="copy-btn">
            <AnchorText onClick={copyToClipboard}> URL 복사</AnchorText>
          </li>
        </CopyUrlFlex>
      </>
    );
  },
);

export default AnniProjectSnsButton;
